// Interfaces and Types

import { AxiosProgressEvent } from 'axios';

// Client Enums must match the keys of the Server enums, instead of the text values
export enum ACTIVE_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

export enum INVITE_STATUS {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  CANCELLED = 'CANCELLED'
}

export enum PLAN_NAME {
  PERSONAL = 'PERSONAL',
  FIRM = 'FIRM'
}

export enum ROLE {
  COMPANY_OWNER = 'COMPANY_OWNER',
  COMPANY_ADMIN = 'COMPANY_ADMIN',
  COMPANY_STAFF = 'COMPANY_STAFF'
}

export enum TEMPLATE_TYPE {
  BLOCK = 'BLOCK',
  BULK = 'BULK',
  REQUEST = 'REQUEST',
  SECTION = 'SECTION'
}

export enum REQUEST_STATUS {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  CLOSED = 'CLOSED',
  ARCHIVED = 'ARCHIVED',
  ARCHIVED_DRAFT = 'ARCHIVED - DRAFT',
  LOCKED = 'LOCKED',
  UNDELIVERED = 'UNDELIVERED'
}

export type CacheableActiveRequestStatus = REQUEST_STATUS.SENT | REQUEST_STATUS.CLOSED | REQUEST_STATUS.LOCKED;

export const CACHEABLE_REQUEST_STATUSES = [
  REQUEST_STATUS.DRAFT,
  REQUEST_STATUS.SENT,
  REQUEST_STATUS.CLOSED,
  REQUEST_STATUS.LOCKED
];
export const CACHEABLE_ACTIVE_REQUEST_STATUSES = [REQUEST_STATUS.SENT, REQUEST_STATUS.CLOSED, REQUEST_STATUS.LOCKED];

export const CACHEABLE_REQUEST_STATUS_COMBINATIONS: REQUEST_STATUS[][] = [
  [REQUEST_STATUS.DRAFT],
  [REQUEST_STATUS.CLOSED],
  [REQUEST_STATUS.SENT, REQUEST_STATUS.LOCKED].sort(),
  [REQUEST_STATUS.SENT, REQUEST_STATUS.LOCKED, REQUEST_STATUS.CLOSED].sort()
];

export enum REQUEST_NOTIFY_OPTION {
  ALL = 'ALL',
  STAFF = 'STAFF',
  CLIENTS = 'CLIENTS',
  ASSIGNEE = 'ASSIGNEE'
}

export const requestNotifyOptionMultiselectLabels = {
  [REQUEST_NOTIFY_OPTION.CLIENTS]: 'All Contacts',
  [REQUEST_NOTIFY_OPTION.STAFF]: 'All Team Members'
};

export const ROLE_LABELS = {
  [ROLE.COMPANY_STAFF]: 'Team Member',
  [ROLE.COMPANY_ADMIN]: 'Admin',
  [ROLE.COMPANY_OWNER]: 'Owner'
};

export const ROLES_FOR_LABEL = Object.entries(ROLE).reduce((acc: Record<string, ROLE>, [key, value]) => {
  acc[key] = value;
  return acc;
}, {});

export const ROLE_ORDER = Object.values(ROLE);

export const requestStatusLabels: Record<REQUEST_STATUS, { color?: string; title: string }> = {
  [REQUEST_STATUS.DRAFT]: { title: 'Draft' },
  [REQUEST_STATUS.SENT]: { color: 'bg-success', title: 'Active' },
  [REQUEST_STATUS.CLOSED]: { color: 'bg-primary', title: 'Complete' },
  [REQUEST_STATUS.ARCHIVED]: { color: 'bg-error', title: 'Archived' },
  [REQUEST_STATUS.ARCHIVED_DRAFT]: { color: 'bg-error', title: 'Archived' },
  [REQUEST_STATUS.LOCKED]: { color: 'bg-warning', title: 'Locked' },
  [REQUEST_STATUS.UNDELIVERED]: { color: 'bg-yellow-600', title: 'Pending' }
};

// Common Model Patterns
export interface CompanyPermissions {
  department?: IDepartment[];
  location?: ILocation[];
}

export interface Deletable {
  deletedAt?: Date;
}

export interface Identifiable {
  _id: string;
}

interface Mutable {
  updatedAt: Date;
}

export interface Nameable {
  name?: string;
}

export interface UserNameable {
  givenName?: string;
  familyName?: string;
}

export interface Emailable {
  email: string;
}

export interface IBlockHistory {
  blockPackage?: Identifiable;
  updatedAt: Date;
  user: IUserOverview;
}

export interface VersionedModel {
  lastUpdate: IBlockHistory;
}

export interface OptionalCompany {
  company?: ICompany;
}

export interface InviteVariant extends Emailable {
  expiresAt: Date;
  isExpired: boolean;
  status: INVITE_STATUS;
  sentAt: Date;
}

interface UserVariant {
  user: IUserOverview;
  company?: ICompanyOverview;
}

// Data Models
export interface IUserOverview extends Identifiable, Deletable, Emailable, UserNameable {}
export interface IUser extends IUserOverview {
  authId?: string;
  isAdmin: boolean;
  phone?: string;
  settings: IUserSettings;
}

export interface IUserSettings {
  activeCompanyId: string | null;
}

export interface IRequestType extends Identifiable, Deletable {
  company?: Identifiable;
  type: string;
  postSubmissionRedirectUrl?: string;
}

export interface ICompanySettings {
  requestTypes: string[];
}

export interface ICompanyOverview extends Identifiable, Deletable, Nameable {}
export interface ICompany extends ICompanyOverview {
  completedOnboarding?: boolean;
  plan: IPlan;
  settings: ICompanySettings;
  defaultPostSubmissionRedirectUrl?: string;
  logoUrl?: string;
  logoAssetId?: IAsset;
}

export interface IMemberOverview extends Identifiable, Deletable, CompanyPermissions, UserVariant, UserNameable {
  roles: ROLE[];
}

export interface IMember extends IMemberOverview {
  user: IUser;
}

export interface IDepartment extends Identifiable, Deletable, Nameable {}
export interface ILocation extends Identifiable, Deletable, Nameable {}

export interface IInviteOverview extends Identifiable, Deletable, CompanyPermissions, InviteVariant, UserNameable {
  code: string;
  roles: ROLE[];
}

export interface IInvite extends IInviteOverview {
  code: string;
  company: ICompanyOverview;
  sentBy?: IUserOverview;
}

export interface IPlan {
  name: PLAN_NAME;
}

export interface IRequestBlockTemplate {
  id?: string;
  data: object;
  type: string;
}

export interface IRequestBlock extends IRequestBlockTemplate {
  id: string;
}

export interface IBaseRequestProps {
  notes?: string;
}

export interface Completion {
  completed: number;
  total: number;
  percentage: number;
}

export interface ICompletable {
  completion?: Completion;
}

export interface IRequestOverview extends Identifiable, Nameable, Mutable, Deletable, CompanyPermissions, ICompletable {
  assignedTo?: IClientOverview;
  clientFinishedWorking: boolean;
  createdBy: UserNameable & Identifiable & Emailable;
  currStaffIsAssigned?: boolean;
  deadline?: Date;
  editable?: boolean;
  entity?: Nameable & Identifiable;
  requestGroup?: Identifiable;
  requestGroupOverview?: IRequestGroupOverview;
  requestType?: IRequestType;
  sentAt?: Date;
  staffEditorIds: Identifiable[];
  staffViewerIds: Identifiable[];
  status?: REQUEST_STATUS;
}

export interface IRequest extends IRequestOverview, IBaseRequestProps, VersionedModel {
  blocks?: IRequestBlock[];
  clientEditorIds?: IClient[];
  clientViewerIds?: IClient[];
  company?: ICompanyOverview;
  staffEditorIds: IMemberOverview[];
  staffViewerIds: IMemberOverview[];
  time?: number;
  version?: string;
  postSubmissionRedirectUrl?: string;
}

export interface IRequestGroupUnauthenticatedOverview
  extends Identifiable,
    Nameable,
    Mutable,
    Deletable,
    CompanyPermissions {
  canSelfServe: boolean;
  company?: ICompanyOverview;
  createdBy: IUser;
  deadline?: Date;
  deletedAt?: Date;
  expiration?: Date;
  isExpired: boolean;
  status: REQUEST_STATUS;
  template: Identifiable;
}

export interface IRequestGroupOverview extends IRequestGroupUnauthenticatedOverview, ICompletable {
  editable: boolean;
  requestType?: IRequestType;
  sentAt?: Date;

  // stats
  active: number;
  archived: number;
  closed: number;
  selfServed: number;
  total: number;
}

export interface IRequestGroup extends IRequestGroupOverview, IBaseRequestProps {
  assignedClients: IClientOverview[];
  assignedStaff: IMemberOverview[];
  requests?: IRequestOverview[];
  postSubmissionRedirectUrl?: string;
}

export interface ITemplateBaseDetails extends Identifiable, Deletable, OptionalCompany, Mutable {
  blockType?: string;
  createdBy?: IUserOverview;
  description?: string;
  title: string;
  type: TEMPLATE_TYPE;
  version: string;
}

export interface ITemplate extends ITemplateBaseDetails, VersionedModel {
  blocks?: IRequestBlockTemplate[];
}

export interface IClientOverview extends Identifiable, UserNameable, Deletable {
  user: IUserOverview;
}

export interface IClient extends IClientOverview, CompanyPermissions, UserVariant {
  company?: Identifiable;

  // These are client only fields, used as a tool to quickly associate Clients with their "associated" Entity Members
  entityMemberId?: string;
  entityDeletedAt?: Date;
}

export interface IClientInvite extends Identifiable, Deletable, CompanyPermissions, InviteVariant {
  entity?: IEntity;
}

export interface IEntityOverview extends Identifiable, Deletable, Nameable {
  status?: ACTIVE_STATUS;
}

export interface IEntity extends IEntityOverview, CompanyPermissions {
  company?: Identifiable;
  mainContact?: IUserOverview;
}

export interface IEntityMember extends Identifiable, Deletable {
  entity: IEntity;
  user: IUserOverview;
}

export interface IEntityWithMembers {
  entity: IEntity;
  members: IEntityMember[];
}

export interface IAsset extends Identifiable, Deletable {
  file: string;
  name: string;
  url: string;
}

export interface IClientInviteSendResult {
  client: IClient;
  entityMember?: IEntityMember;
  sentAt?: Date;
}

export interface ISendClientProps extends Emailable {
  department?: string[];
  entityId?: string;
  location?: string[];
  name?: string;
}

export interface IRequestTokenParams {
  requestToken?: string;
}

export interface IAccessToken extends Identifiable, Deletable, UserVariant {
  expiryDate?: Date;
}

export interface IDownloadFileParams extends IRequestTokenParams {
  Authorization: string | null;
  onDownloadProgress: (progressEvent: AxiosProgressEvent) => void;
}

// Response types

export interface IBlockPackageUpdateResponse {
  error?: string;
  request?: IRequest;
  template?: ITemplate;
  updated: boolean;
}
